/* Acquisition CTA contract for the shared Bank shell.
 *
 * Two authorities decide what these controls may say, and nothing else does.
 * Entitlement decides whether an active member sees them: active members
 * receive account/benefit links instead, and any untagged Join/Get-50%
 * acquisition control on Home or a PDP is suppressed for them. Enrollment
 * readiness decides whether a nonmember may be invited at all: while the
 * server offer is closed or has not been fetched yet, no acquisition claim is
 * shown, because the storefront cannot honour it. A signed-in customer without
 * active benefits is treated exactly like a guest.
 *
 * CLOSED IS THE SERVER STATE, NOT A CORRECTION
 * --------------------------------------------
 * The shell used to server-render "Join Pup Club" and "Get 50% off all
 * products" and rely on this file to take them away. That made the closed
 * claim part of the response: it was visible before this ran, and it survived
 * entirely if this file failed to load. snippets/pk-vip-shell.liquid and
 * snippets/pm-vip-page-content.liquid now ship those elements empty and hidden,
 * and the authored wording lives here. This file writes it in only after
 * PKPupClub.freshOfferReadiness() -- a forced network read of the published
 * offer, evaluated against the one canonical enrollment contract -- has come
 * back ready. No club script, a failed fetch, a closed contract, an unknown
 * state or a disabled provider therefore leave the page exactly as the server
 * sent it, which is with no acquisition control at all.
 *
 * This file deliberately does not alter prices, carts, or checkout behavior.
 */
(() => {
  'use strict';

  /* Meta's ad view is never a membership surface and owns its own shell. */
  if (document.documentElement.dataset.pkEntry === 'ad') return;

  const MEMBER_ROUTE = '/?view=pup-club#benefits';
  const OFFER_ROUTE = '/?view=pup-club#offer';
  const SHOP_ROUTE = '/?view=pup-club#catalog';
  const ACQUISITION_COPY = /(?:\bjoin(?: the)? pup club\b|\bget 50% off\b|\bstart my free trial\b)/i;
  const contextualOriginals = new WeakMap();
  const contextualDisplay = new WeakMap();
  let renderQueued = false;

  /* The authored acquisition wording, held here rather than in the response.
     One entry per tagged control kind, so the shell markup stays neutral. */
  const OPEN_COPY = {
    nav: {
      html: 'Join Pup Club',
      href: OFFER_ROUTE,
      ariaLabel: null,
    },
    banner: {
      html: '<span>Join the Pup Club!</span> <span class="pk-kind-line__cta">Members save 50% on everything</span> <span aria-hidden="true">&rarr;</span>',
      href: '/pages/pup-club',
      ariaLabel: 'Join the Pup Club. Members save 50% on everything',
    },
    invitation: {
      html: 'Join Pup Club →',
      href: OFFER_ROUTE,
      ariaLabel: null,
    },
  };

  /* The existing benefits relabel, for a verified active member only. */
  const MEMBER_COPY = {
    nav: {
      html: 'Pup Club benefits',
      href: MEMBER_ROUTE,
      ariaLabel: 'View Pup Club benefits',
    },
    banner: {
      html: '<span>Pup Club active</span> <span class="pk-kind-line__cta">View your benefits</span> <span aria-hidden="true">&rarr;</span>',
      href: MEMBER_ROUTE,
      ariaLabel: 'Pup Club active. View your benefits',
    },
    invitation: {
      html: 'View my benefits →',
      href: MEMBER_ROUTE,
      ariaLabel: 'View Pup Club benefits',
    },
  };

  /* Pup Club is in the cart but not bought yet (Zack, 2026-09-27): the shopper
     has joined, so the Join pitch turns into "your Pup Club prices are on" and
     points at the shop. pk-club-cart-state.js publishes the cart answer. */
  const JOINED_COPY = {
    nav: null,
    banner: {
      html: '<span>Pup Club is in your cart!</span> <span class="pk-kind-line__cta">Your 50% off prices are on</span> <span aria-hidden="true">&rarr;</span>',
      href: SHOP_ROUTE,
      ariaLabel: 'Pup Club is in your cart. Your 50% off prices are on. Shop now',
    },
    invitation: {
      html: 'Shop at 50% off →',
      href: SHOP_ROUTE,
      ariaLabel: null,
    },
  };

  /* 2026-10-08: one identity read (snippets/pk-demo-action-lock.liquid). The test
     account is shown as an existing member. A session still being verified, or a
     signed-in account, never sees a join or "Pup Club is in your cart" claim. */
  const identity = () => {
    const lock = window.PKDemoActionLock;
    if (lock && typeof lock.identity === 'function') { try { return lock.identity(); } catch (_) { return 'resolving'; } }
    return 'guest';
  };
  const demoSignedIn = () => {
    try { return identity() === 'demo' && window.PKDemoActionLock.isSignedIn() === true; } catch (_) { return false; }
  };
  const isActiveMember = () => (window.PK_PUP_CLUB_AUTH?.authenticated === true && window.PK_PUP_CLUB_AUTH?.activeMember === true) || demoSignedIn();
  const clubInCart = () => window.PK_CLUB_IN_CART === true && window.PKPupClub?.isReviewAccount?.() !== true && identity() === 'guest';

  const club = () => window.PKPupClub || null;

  /* Asked of pk-vip-pup-club.js, never recomputed here. No club script, a failed
     offer fetch, an unfetched offer or any false clause is "not open", and not
     open means no acquisition claim is shown. */
  const contractReady = () => {
    const api = club();
    if (!api || typeof api.offerReadiness !== 'function') return false;
    try { return api.offerReadiness().ready === true; } catch (_) { return false; }
  };

  /* Only a completed successful forced refresh may reveal an acquisition claim.
     It starts false on every page view. One automatic verification per view is
     enough here: this file only paints, and every path that actually mutates a
     cart, a checkout or a membership forces its own refresh at the moment it
     acts. */
  let freshVerified = false;
  let freshPending = false;
  let freshChecked = false;
  function verifyFreshReadiness() {
    if (freshPending || freshChecked) return;
    const api = club();
    /* No club yet, or a build without the forced gate: stay closed and leave
       the latch open so the club's own offer-state event can retry. */
    if (!api || typeof api.freshOfferReadiness !== 'function') return;
    freshPending = true;
    Promise.resolve(api.freshOfferReadiness())
      .then((readiness) => { freshVerified = !!(readiness && readiness.ready === true); })
      .catch(() => { freshVerified = false; })
      .finally(() => {
        freshPending = false;
        freshChecked = true;
        scheduleRender();
      });
  }

  const isEnrollmentOpen = () => freshVerified && contractReady();

  /* 'member' relabels the control to benefits, 'open' writes the authored
     acquisition copy in, 'closed' is the neutral state the server rendered. */
  /* A sign-in form is an authentication surface: no enrollment or cart-membership banner there.
     That is the #signin route, and any other portal view that renders a sign-in form in
     place, such as #account straight after Sign out. */
  const SIGNIN_FORMS = '#pk-signin-form, #pk-chat-signin-form';
  const authSurface = () => {
    if (!document.querySelector('[data-pk-vip-portal]')) return false;
    if (/^#signin(?:$|[?&/])/.test(location.hash)) return true;
    return Array.from(document.querySelectorAll(SIGNIN_FORMS)).some(form => !form.closest('[hidden]'));
  };
  const ctaState = () => {
    if (isActiveMember()) return 'member';
    const who = identity();
    if (who === 'resolving' || who === 'account' || who === 'demo') return 'closed';
    if (authSurface()) return 'closed';
    return clubInCart() ? 'joined' : 'open';
  };

  function snapshot(node) {
    return {
      html: node.innerHTML,
      href: node.getAttribute('href'),
      ariaLabel: node.getAttribute('aria-label'),
      ariaHidden: node.getAttribute('aria-hidden'),
      tabindex: node.getAttribute('tabindex'),
      hidden: node.hidden,
    };
  }

  function restoreAttribute(node, name, value) {
    if (value === null) node.removeAttribute(name);
    else node.setAttribute(name, value);
  }

  function restore(node, original) {
    node.innerHTML = original.html;
    restoreAttribute(node, 'href', original.href);
    restoreAttribute(node, 'aria-label', original.ariaLabel);
    restoreAttribute(node, 'aria-hidden', original.ariaHidden);
    restoreAttribute(node, 'tabindex', original.tabindex);
    node.hidden = original.hidden;
  }

  /* The closed state: empty, hidden, aria-hidden and out of the tab order.
     Identical to what the server rendered, so returning to it is exact. */
  function neutralise(node) {
    node.innerHTML = '';
    node.hidden = true;
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('tabindex', '-1');
    node.removeAttribute('aria-label');
  }

  function renderTagged(node, state) {
    /* The server ships every tagged control as 'closed', so a closed page makes
       no DOM write at all and the mutation observer below cannot loop. */
    const applied = node.dataset.pkMemberCtaState || 'closed';
    if (applied === state) return;

    if (state === 'closed') {
      neutralise(node);
      node.dataset.pkMemberCtaState = 'closed';
      return;
    }

    const kind = node.dataset.pkMemberAcquisition || 'nav';
    const table = state === 'member' ? MEMBER_COPY : state === 'joined' ? JOINED_COPY : OPEN_COPY;
    const copy = table[kind] || table.nav;
    /* No menu entry while Pup Club is in the cart: Shop already sits above it. */
    if (!copy) {
      neutralise(node);
      node.dataset.pkMemberCtaState = state;
      return;
    }
    node.innerHTML = copy.html;
    node.setAttribute('href', copy.href);
    if (copy.ariaLabel) node.setAttribute('aria-label', copy.ariaLabel);
    else node.removeAttribute('aria-label');
    node.removeAttribute('aria-hidden');
    node.removeAttribute('tabindex');
    node.hidden = false;
    node.dataset.pkMemberCtaState = state;
  }

  /* A block is a container whose whole purpose is acquisition — today, the
     shell invitation. It is revealed by the same verdict that reveals the
     control inside it, and hidden is its server state. */
  function renderBlock(node, state) {
    const applied = node.dataset.pkAcquisitionState || 'closed';
    if (applied === state) return;
    const reveal = state !== 'closed' && state !== 'joined';
    node.hidden = !reveal;
    if (reveal) node.removeAttribute('aria-hidden');
    else node.setAttribute('aria-hidden', 'true');
    node.dataset.pkAcquisitionState = state;
  }

  /* The native Pup Club page enhancement.
     This page is not hidden when closed: its closed state is the page. The
     server renders an explanation and ordinary account/help routes, with no
     trial, no renewal price and no start action, and that is what stays up
     under every closed, unknown, missing-script, fetch-failed or
     provider-disabled state. Only after the same forced readiness fetch comes
     back ready does the open part appear, and the terms sentence it shows is
     built by pk-vip-pup-club.js from the server's own published numbers, never
     authored here or in the page. A member sees the neutral page: they are not
     an acquisition audience and the page has no member view of its own. */
  function renderPupClubPage(node, state) {
    const api = club();
    const sentence = (state === 'open' || state === 'joined') && api && typeof api.offerSentence === 'function'
      ? api.offerSentence()
      : '';
    const ready = (state === 'open' || state === 'joined') && !!sentence;
    const applied = node.dataset.pkPupClubPage === 'open';
    if (applied === ready) return;
    const closed = node.querySelector('[data-pk-pup-club-closed]');
    const open = node.querySelector('[data-pk-pup-club-open]');
    const terms = node.querySelector('[data-pk-pup-club-terms]');
    if (terms) terms.textContent = ready ? sentence : '';
    if (closed) closed.hidden = ready;
    if (open) open.hidden = !ready;
    node.dataset.pkPupClubPage = ready ? 'open' : 'closed';
  }

  function isHomeOrPdpAcquisition(node) {
    if (node.hasAttribute('data-pk-member-acquisition')) return false;
    if (node.closest('.pk-vip-portal')) return false;
    // A cart selection is state, not a second acquisition CTA. Keep its selected option visible.
    if (node.closest('[data-pm-cart-club-page], [data-pm-cart-club]')) return false;
    return ACQUISITION_COPY.test(node.textContent || '');
  }

  function renderContextual(node, active) {
    if (!contextualOriginals.has(node)) {
      contextualOriginals.set(node, snapshot(node));
      contextualDisplay.set(node, { value: node.style.getPropertyValue('display'), priority: node.style.getPropertyPriority('display') });
    }
    const applied = node.dataset.pkMemberCtaState === 'suppressed';

    if (!active) {
      if (applied) {
        restore(node, contextualOriginals.get(node));
        const original = contextualDisplay.get(node);
        if (original?.value) node.style.setProperty('display', original.value, original.priority);
        else node.style.removeProperty('display');
      }
      delete node.dataset.pkMemberCtaState;
      return;
    }
    // Existing button CSS may set display:flex, overriding the HTML hidden attribute.
    node.style.setProperty('display', 'none', 'important');
    if (applied) return;

    node.hidden = true;
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('tabindex', '-1');
    node.dataset.pkMemberCtaState = 'suppressed';
  }

  function render() {
    renderQueued = false;
    const state = ctaState();
    const active = state === 'member' || state === 'joined';

    document.querySelectorAll('[data-pk-acquisition-block]').forEach(node => renderBlock(node, state));
    document.querySelectorAll('[data-pk-pup-club-page]').forEach(node => renderPupClubPage(node, state));
    document.querySelectorAll('[data-pk-member-acquisition]').forEach(node => renderTagged(node, state));
    /* Untagged Home/PDP controls stay on the entitlement rule only. The static
       scan found no untagged closed-state acquisition claim on a Bank template,
       so widening this selector would only risk hiding unrelated copy. */
    document.querySelectorAll('#MainContent a, #MainContent button, .pk-club-btn').forEach(node => {
      if (contextualOriginals.has(node) || isHomeOrPdpAcquisition(node)) renderContextual(node, active);
    });
  }

  function scheduleRender() {
    if (renderQueued) return;
    renderQueued = true;
    queueMicrotask(render);
  }

  render();
  addEventListener('pk:member-state', scheduleRender);
  addEventListener('pk:auth-state', scheduleRender);
  /* The published offer lands after first paint, so re-decide when it does. */
  addEventListener('pk:offer-state', scheduleRender);
  addEventListener('pk:club-cart', scheduleRender);
  addEventListener('pk:identity', scheduleRender);
  addEventListener('hashchange', scheduleRender);
  /* A page restored from the back/forward cache carries the verdict it was
     serialized with. Drop it and verify again before anything may reopen. */
  addEventListener('pageshow', (event) => {
    if (event.persisted) { freshVerified = false; freshChecked = false; }
    scheduleRender();
  });
  new MutationObserver(scheduleRender).observe(document.body, { childList: true, subtree: true });
})();
